/**
 * Документы — оригинальные файлы из «Приложения» системы «Складской учет 20».
 * Бланки накладных, шаблоны печатных форм, журналы заказов, исходные xlsx
 * остатков и архив. Можно скачать оригинал или расшифрованную копию (для
 * запароленных xlsx используем пароль из VBA).
 */
import { useQuery } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { fetchBlanks, blankDownloadUrl } from "@/api/warehouse";
import { ApiError } from "@/api/client";


function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("ru-RU");
  } catch { return iso; }
}


export function WarehouseDocumentsPage() {
  const q = useQuery({
    queryKey: ["warehouse-blanks"],
    queryFn: () => fetchBlanks(),
  });

  if (q.isLoading) return <Spinner size="lg" label="Загрузка документов…" />;
  if (q.error) {
    const err = q.error;
    if (err instanceof ApiError && err.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ ограничен</div></div>;
    }
    return <div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div>;
  }

  const data = q.data!;

  if (data.root_missing) {
    return (
      <div className="page">
        <div className="alert alert-amber">
          <div className="alert-icon"><Icon name="warn" size={16}/></div>
          <div className="alert-body">
            <div className="alert-title">Файлы Приложений недоступны</div>
            <div style={{ marginTop: 4 }}>
              На сервере нет распакованного архива в <code>{data.root}</code>.
              Свяжитесь с админом, чтобы файлы были скопированы.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Группировка по категории
  const grouped: Record<string, typeof data.items> = {};
  for (const f of data.items) {
    if (!grouped[f.category]) grouped[f.category] = [];
    grouped[f.category].push(f);
  }
  const CATEGORY_ORDER = [
    "Рабочие книги", "Бланки", "Шаблоны", "Заказы",
    "Склады", "Справочники", "Архив — Приход", "Архив — Отгрузка",
  ];
  const sortedCategories = Object.keys(grouped).sort((a, b) => {
    const ia = CATEGORY_ORDER.indexOf(a);
    const ib = CATEGORY_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  return (
    <div className="page">

      <div className="card" style={{ marginBottom: "var(--gap)" }}>
        <div className="card-h">
          <div>
            <h3 className="card-title">Документы (Приложения)</h3>
            <div className="card-sub">
              Оригинальные xlsx/xlsm из системы «Складской учет 20». Зашифрованные
              файлы скачиваются расшифрованными.
            </div>
          </div>
          <div className="muted" style={{ fontSize: 12 }}>Всего: {data.items.length}</div>
        </div>
      </div>

      {sortedCategories.map(cat => (
        <div key={cat} className="card" style={{ marginBottom: "var(--gap)" }}>
          <div className="card-h">
            <h3 className="card-title">{cat}</h3>
            <div className="muted" style={{ fontSize: 12 }}>{grouped[cat].length} файл(ов)</div>
          </div>
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Имя файла</th>
                  <th style={{ width: 100 }}>Тип</th>
                  <th style={{ width: 100 }} className="num">Размер</th>
                  <th style={{ width: 120 }}>Изменён</th>
                  <th style={{ width: 220 }}>Скачать</th>
                </tr>
              </thead>
              <tbody>
                {grouped[cat].map(f => (
                  <tr key={f.key}>
                    <td>
                      <div className="strong">{f.name}</div>
                      {f.category_path && (
                        <div style={{ fontSize: 11, color: "var(--mute)" }}>{f.category_path}</div>
                      )}
                    </td>
                    <td className="tabular" style={{ fontSize: 12, color: "var(--mute)" }}>{f.ext}</td>
                    <td className="num tabular" style={{ fontSize: 12 }}>{formatSize(f.size_bytes)}</td>
                    <td className="tabular" style={{ fontSize: 12 }}>{formatDate(f.modified)}</td>
                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        <a
                          className="btn btn-sm btn-primary"
                          href={blankDownloadUrl(f.key, true)}
                          download={f.name}
                          target="_blank" rel="noopener"
                        >
                          <Icon name="download" size={12}/> Скачать
                        </a>
                        <a
                          className="btn btn-sm"
                          href={blankDownloadUrl(f.key, false)}
                          download={f.name}
                          target="_blank" rel="noopener"
                          title="Скачать оригинальный (с шифрованием)"
                        >
                          оригинал
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
